const sessionTimeFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York', weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});

export const filterSignalChartCandles = (candles, interval, includeExtendedHours = false) => {
  if (!Array.isArray(candles)) return [];
  if (includeExtendedHours || !/^(?:1|2|5|15|30|60|90)m$|^1h$/.test(String(interval || '').trim().toLowerCase())) return candles;
  return candles.filter(candle => {
    const date = new Date(candle?.time);
    if (!Number.isFinite(date.getTime())) return false;
    const parts = Object.fromEntries(sessionTimeFormatter.formatToParts(date).map(part => [part.type, part.value]));
    const minutes = Number(parts.hour) * 60 + Number(parts.minute);
    return !['Sat', 'Sun'].includes(parts.weekday) && minutes >= 570 && minutes < 960;
  });
};

export const formatEntryToTargetPct = (entry, target) => {
  if (entry == null || target == null || entry === '' || target === '') return '-';
  const entryPrice = Number(entry);
  const targetPrice = Number(target);
  if (!Number.isFinite(entryPrice) || !Number.isFinite(targetPrice)
      || entryPrice <= 0 || targetPrice <= 0) return '-';

  const percentage = ((targetPrice - entryPrice) / entryPrice) * 100;
  return `${percentage > 0 ? '+' : ''}${percentage.toFixed(2)}%`;
};

export const getEntryTargetMeasurement = (entry, target, yScale, top, bottom) => {
  const percentage = formatEntryToTargetPct(entry, target);
  if (percentage === '-') return null;

  const rawEntryY = yScale(Number(entry));
  const rawTargetY = yScale(Number(target));
  const clamp = (value) => Math.max(top, Math.min(bottom, value));
  return {
    entryY: clamp(rawEntryY),
    targetY: clamp(rawTargetY),
    offScale: rawEntryY < top || rawEntryY > bottom
      || rawTargetY < top || rawTargetY > bottom,
    percentage,
  };
};

export const getEntryStopMeasurement = (entry, stop, yScale, top, bottom) => {
  const measurement = getEntryTargetMeasurement(entry, stop, yScale, top, bottom);
  if (!measurement) return null;
  const { targetY, ...shared } = measurement;
  return { ...shared, stopY: targetY };
};

const breakoutTimeFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});

export const findBreakoutCandleIndex = (candles, breakoutTime) => {
  if (!Array.isArray(candles) || typeof breakoutTime !== 'string') return -1;
  const value = breakoutTime.trim();
  const wallTime = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}$/.test(value)
    ? value.replace('T', ' ') : null;
  const epoch = /(?:Z|[+-]\d{2}:\d{2})$/i.test(value) ? Date.parse(value) : NaN;
  if (!wallTime && !Number.isFinite(epoch)) return -1;
  const matches = candles.map((candle, index) => {
    const date = new Date(candle.time);
    if (!Number.isFinite(date.getTime())) return -1;
    if (!wallTime) return date.getTime() === epoch ? index : -1;
    const parts = Object.fromEntries(breakoutTimeFormatter.formatToParts(date).map(part => [part.type, part.value]));
    const candleTime = `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;
    return candleTime === wallTime ? index : -1;
  }).filter(index => index >= 0);
  return matches.length === 1 ? matches[0] : -1;
};

export const findEntryCandleIndex = (candles, entry, retestTimestamp = null) => {
  if (entry == null || entry === '') return -1;
  const entryPrice = Number(entry);
  if (!Array.isArray(candles) || !Number.isFinite(entryPrice) || entryPrice <= 0) return -1;

  const earliestTimestamp = Number.isFinite(retestTimestamp) ? retestTimestamp : null;
  const matchTolerance = Math.max(Math.abs(entryPrice) * 1e-6, 0.00005);
  let closestIndex = -1;
  let closestDiff = Number.POSITIVE_INFINITY;
  let matchingIndex = -1;
  let matchingDiff = Number.POSITIVE_INFINITY;

  candles.forEach((candle, index) => {
    if (candle?.close == null || candle.close === '') return;
    const close = Number(candle.close);
    if (!Number.isFinite(close)) return;

    if (earliestTimestamp !== null) {
      const candleTimestamp = new Date(candle.time).getTime();
      if (Number.isFinite(candleTimestamp) && candleTimestamp < earliestTimestamp) return;
    }

    const diff = Math.abs(close - entryPrice);
    if (diff < closestDiff) {
      closestDiff = diff;
      closestIndex = index;
    }
    if (diff <= matchTolerance && diff < matchingDiff) {
      matchingDiff = diff;
      matchingIndex = index;
    }
  });

  return matchingIndex >= 0 ? matchingIndex : closestIndex;
};

export const findPredictionEntryCandleIndex = (candles, entry, { confirmationTime, retestTime } = {}) => {
  if (confirmationTime) return findBreakoutCandleIndex(candles, confirmationTime);
  if (!Array.isArray(candles)) return -1;
  if (!retestTime) return findEntryCandleIndex(candles, entry);
  const wallTime = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}$/.test(retestTime)
    ? retestTime.replace('T', ' ') : null;
  const epoch = /(?:Z|[+-]\d{2}:\d{2})$/i.test(retestTime) ? Date.parse(retestTime) : NaN;
  const candidates = candles.map((candle, index) => ({ candle, index })).filter(({ candle }) => {
    const date = new Date(candle.time);
    if (!Number.isFinite(date.getTime())) return false;
    if (!wallTime) return !Number.isFinite(epoch) || date.getTime() >= epoch;
    const parts = Object.fromEntries(breakoutTimeFormatter.formatToParts(date).map(part => [part.type, part.value]));
    return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}` >= wallTime;
  });
  const match = findEntryCandleIndex(candidates.map(({ candle }) => candle), entry);
  return match < 0 ? -1 : candidates[match].index;
};

export const calculateSimpleMovingAverage = (candles, period = 200) => {
  if (!Number.isInteger(period) || period <= 0) {
    throw new RangeError('Moving average period must be a positive integer.');
  }
  if (!Array.isArray(candles)) return [];

  const averages = [];
  let sum = 0;
  let invalidCount = 0;

  candles.forEach((candle, index) => {
    const rawClose = candle?.close;
    const close = rawClose === null || rawClose === undefined || rawClose === ''
      ? null
      : Number(rawClose);

    if (close === null || !Number.isFinite(close)) {
      invalidCount += 1;
    } else {
      sum += close;
    }

    if (index >= period) {
      const expiredRawClose = candles[index - period]?.close;
      const expiredClose = expiredRawClose === null || expiredRawClose === undefined || expiredRawClose === ''
        ? null
        : Number(expiredRawClose);
      if (expiredClose === null || !Number.isFinite(expiredClose)) {
        invalidCount -= 1;
      } else {
        sum -= expiredClose;
      }
    }

    averages.push(index >= period - 1 && invalidCount === 0 ? sum / period : null);
  });

  return averages;
};

export const selectSignalChartCandles = (allCandles, start, end, renderStep, entryIndex, labelTimes = new Set()) => {
  const candles = [];
  let visibleEntryIndex = -1;
  for (let index = start; index < end; index += 1) {
    if ((index - start) % renderStep !== 0 && index !== end - 1 && index !== entryIndex
        && !labelTimes.has(allCandles[index].time)) continue;
    if (index === entryIndex) visibleEntryIndex = candles.length;
    candles.push(allCandles[index]);
  }
  return { candles, entryIndex: visibleEntryIndex };
};
